import React from 'react'
import PropTypes from 'prop-types'
import { DialogOverlay, DialogOverlayActions, Separator, Button, DangerButton } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { getPersonName } from '@fs/zion-sls'

/**
 * ConfirmUnlinkEvent
 *
 * Dialog overlay to confirm unlinking a person from an event.
 * Shows the person's name and asks for confirmation before removing them.
 *
 * @param {Object} overlay - Dialog overlay props from useOverlay hook
 * @param {Object} person - The person to be unlinked from the event
 * @param {Object} event - The event object
 * @param {Function} onRemove - Callback when removal is confirmed
 */
const ConfirmUnlinkEvent = ({ overlay, person, event, onRemove }) => {
  const [t] = useTranslation()

  if (!person || !event) {
    return null
  }

  return (
    <DialogOverlay {...overlay} title={t('edit-events.confirm-unlink-name.title', 'Remove Event from Name')}>
      {t('edit-events.confirm-unlink-name.message', 'Are you sure you would like to remove this event from:')}
      <Separator size="sm" />
      {getPersonName({ person })}
      <Separator size="sm" />
      <DialogOverlayActions>
        <Button
          emphasis="medium"
          data-testid="cancel-unlink-event-from-name"
          linkName="Events Cancel Unlink Name"
          onClick={overlay.close}
        >
          {t('edit-events.confirm-unlink-name.cancel.btn', 'Cancel')}
        </Button>
        <DangerButton
          data-testid="confirm-unlink-event-from-name"
          linkName="Events Confirm Unlink Name"
          onClick={onRemove}
        >
          {t('edit-events.confirm-unlink-name.remove.btn', 'Remove')}
        </DangerButton>
      </DialogOverlayActions>
    </DialogOverlay>
  )
}

ConfirmUnlinkEvent.propTypes = {
  overlay: PropTypes.shape({
    close: PropTypes.func,
  }).isRequired,
  person: PropTypes.shape({
    id: PropTypes.string,
  }),
  event: PropTypes.shape({
    id: PropTypes.string,
  }),
  onRemove: PropTypes.func.isRequired,
}

export default ConfirmUnlinkEvent
